import React from "react";
import { formatNumberWithCommas } from "../../utils";

const CommissionPriceRanges = ({ priceRanges = [], onPriceRangeChange }) => {
  // Estado local para manejar los valores de los inputs mientras el usuario escribe
  const [localValues, setLocalValues] = React.useState(() => {
    const initial = {};
    priceRanges.forEach((range, index) => {
      initial[index] = range.porcentaje || 0;
    });
    return initial;
  });
  // Ref para rastrear qué campos están siendo editados
  const editingRef = React.useRef({});

  // Sincronizar valores locales con props cuando cambien (solo si no se están editando)
  React.useEffect(() => {
    setLocalValues((prev) => {
      const newLocalValues = { ...prev };
      priceRanges.forEach((range, index) => {
        // Solo actualizar si el campo no está siendo editado
        if (!editingRef.current[index]) {
          newLocalValues[index] = range.porcentaje || 0;
        }
      });
      return newLocalValues;
    });
  }, [priceRanges]);

  const handlePercentageChange = (index, newPercentage) => {
    if (!onPriceRangeChange) return;

    // Marcar que este campo está siendo editado
    editingRef.current[index] = true;

    // Actualizar el valor local inmediatamente para que el input responda
    setLocalValues((prev) => ({
      ...prev,
      [index]: newPercentage === "" ? "" : newPercentage,
    }));

    const updatedRanges = [...priceRanges];
    const parsedPercentage = parseFloat(newPercentage);

    // Solo actualizar el estado padre si el valor es válido
    if (!isNaN(parsedPercentage) && parsedPercentage >= 0) {
      updatedRanges[index] = {
        ...updatedRanges[index],
        porcentaje: parsedPercentage,
      };
      onPriceRangeChange(updatedRanges);
    }
  };

  const handleBlur = (index, value) => {
    // Marcar que este campo ya no está siendo editado
    editingRef.current[index] = false;

    const parsedValue = parseFloat(value);
    // Si el valor es inválido, restaurar el valor original
    if (isNaN(parsedValue) || parsedValue < 0) {
      setLocalValues((prev) => ({
        ...prev,
        [index]: priceRanges[index]?.porcentaje || 0,
      }));
    } else {
      // Asegurar que el valor esté sincronizado
      setLocalValues((prev) => ({
        ...prev,
        [index]: parsedValue,
      }));
    }
  };

  return (
    <div className="table-responsive mb-3">
      <table className="table table-hover table-panel text-nowrap align-middle mb-0">
        <thead className="sticky-thead table-light text-center">
          <tr>
            <th className="align-middle">Mínimo</th>
            <th className="align-middle">Máximo</th>
            <th className="align-middle">Porcentaje de ganancia</th>
          </tr>
        </thead>
        <tbody>
          {priceRanges.length > 0 ? (
            priceRanges.map((range, index) => (
              <tr key={index} className="text-center">
                <td>${formatNumberWithCommas(range.minimo) || "0.00"}</td>
                <td>
                  {range.maximo === null || range.maximo === undefined
                    ? "En adelante"
                    : `$${formatNumberWithCommas(range.maximo)}`}
                </td>
                <td>
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: "80px", textAlign: "center" }}
                      value={
                        localValues[index] !== undefined
                          ? localValues[index]
                          : range.porcentaje || "0"
                      }
                      min="0"
                      step="0.1"
                      onChange={(e) =>
                        handlePercentageChange(index, e.target.value)
                      }
                      onBlur={(e) => handleBlur(index, e.target.value)}
                      onWheel={(e) => e.target.blur()}
                    />
                    <span>%</span>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center text-muted">
                No hay rangos de precios configurados
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommissionPriceRanges;
